/**
 * Port of {@class IamCelCompiler} —
 * backend/src/main/java/com/irian/iam/ruleengine/cel/IamCelCompiler.java
 *
 * Wraps @bufbuild/cel parse() with the IAM schema. The Java implementation
 * holds a cel-java CelCompiler configured with options (heterogeneous numeric
 * comparisons, cel.bind extension, STANDARD_MACROS); @bufbuild/cel exposes a
 * single parse() function that handles macros internally — no compiler config
 * needed for parse. rebuild() exists for compatibility but is a no-op in TS
 * since there is no compiler state to refresh.
 */

import {parse} from '@bufbuild/cel'
import type {ParsedExpr} from '@bufbuild/cel-spec/cel/expr/syntax_pb.js'
import {IamRuleExpressionSchema} from './IamRuleExpressionSchema'
import {IamCelValidator} from './IamCelValidator'
import {RuleParseException} from '../api/RuleParseException'

export class IamCelCompiler {
    private readonly schemaField: IamRuleExpressionSchema
    private readonly validator: IamCelValidator

    constructor(schema?: IamRuleExpressionSchema) {
        this.schemaField = schema ?? IamRuleExpressionSchema.iamMultiRoot()
        this.validator = new IamCelValidator(this.schemaField)
    }

    schema(): IamRuleExpressionSchema {
        return this.schemaField
    }

    /**
     * Parse a CEL source string. Returns the parsed AST or throws on syntax
     * error. Mirrors the Java compiler().compile(source).getAst() entry path.
     *
     * <p>Issue 982 case C — after @bufbuild/cel accepts the source, the IAM
     * validator walks the AST and rejects unknown attribute keys (typo,
     * removed definition, etc.). Throws {@link RuleParseException} so
     * unknown-attribute references never reach evaluation.
     */
    parse(source: string): ParsedExpr {
        const parsed = parse(source)
        const errors = this.validator.validate(parsed)
        if (errors.length > 0) {
            throw new RuleParseException(errors[0], source, -1)
        }
        return parsed
    }

    /**
     * Java rebuilds the cel-java type set on attribute-definition change. In TS
     * the schema's dynamic attribute folding is queried at suggestion time, so
     * there's no compiler state to refresh — just rebuild the schema's snapshot.
     */
    rebuild(): void {
        this.schemaField.rebuild()
    }
}
